import React, { FC, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Rendered to the right of the option -- the faceted count for this value. */
  count?: string;
}

export interface MultiSelectFilterProps {
  options: MultiSelectOption[];
  /** null = nothing picked, so the default applies. A Set is an explicit selection. */
  selection: Set<string> | null;
  onChange: (next: Set<string> | null) => void;
  /** Values unchecked while the selection is null. */
  hiddenByDefault?: (value: string) => boolean;
  noun: string;
  title?: string;
}

/**
 * The hub and client pickers.
 *
 * The one thing worth knowing about them: null selection is not the same as "everything ticked".
 * Null means "follow the default", so a client that first appears on the board after a refresh is
 * included -- or, if it is one of the default-hidden ones, still excluded -- without the user
 * re-picking. Ticking exactly the default set therefore drops back to null rather than freezing
 * today's list.
 */
const MultiSelectFilter: FC<MultiSelectFilterProps> = ({
  options,
  selection,
  onChange,
  hiddenByDefault,
  noun,
  title,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [find, setFind] = useState("");

  const defaultOn = (v: string) => !(hiddenByDefault ? hiddenByDefault(v) : false);
  const checked = (v: string) => (selection ? selection.has(v) : defaultOn(v));

  const isDefault = (on: string[]) =>
    on.length === options.filter((o) => defaultOn(o.value)).length && on.every((v) => defaultOn(v));

  const commit = (on: string[]) => onChange(isDefault(on) ? null : new Set(on));

  const toggle = (v: string) => {
    const on = options.map((o) => o.value).filter((x) => (x === v ? !checked(x) : checked(x)));
    commit(on);
  };

  const label = useMemo(() => {
    if (!selection) {
      const hidden = options.filter((o) => !defaultOn(o.value)).map((o) => o.label);
      return hidden.length ? `All but ${hidden.join(", ")}` : `All ${noun}`;
    }
    if (selection.size === 0) return `No ${noun} selected`;
    if (selection.size <= 2) {
      return [...selection].map((v) => options.find((o) => o.value === v)?.label ?? v).join(", ");
    }
    return `${selection.size} of ${options.length} ${noun}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, options, noun]);

  const shown = find ? options.filter((o) => o.label.toLowerCase().includes(find.trim().toLowerCase())) : options;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color={selection ? "info" : "secondary"}
        endIcon={<ArrowDropDownIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        title={title}
        aria-haspopup="listbox"
        aria-expanded={!!anchor}
        sx={{ textTransform: "none", justifyContent: "space-between", minWidth: 0, flex: 1, fontSize: 12 }}
      >
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
        </Box>
      </Button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        // A full-height sheet on narrow screens, a dropdown on a desktop -- the option list is
        // long enough that a small popover is unusable on a tablet.
        // backgroundColor is set explicitly because Flow's theme
        // (assets/theme/components/popover.ts) overrides every Popover paper to transparent, which
        // left this dropdown showing the map straight through its own option list. It has to go on
        // the paper slot rather than on an ancestor: MUI portals a Popover onto document.body, so
        // nothing scoped to the board's subtree can reach it.
        slotProps={{
          paper: {
            sx: { width: { xs: "88vw", sm: 320 }, maxHeight: "70vh", p: 1, backgroundColor: "background.paper" },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "capitalize" }}>{noun}</Typography>
          <Button size="small" sx={{ ml: "auto", fontSize: 11 }} onClick={() => commit(options.map((o) => o.value))}>
            Select all
          </Button>
          <Button size="small" sx={{ fontSize: 11 }} onClick={() => commit([])}>
            None
          </Button>
          <Button size="small" sx={{ fontSize: 11 }} onClick={() => setAnchor(null)}>
            Done
          </Button>
        </Box>
        <TextField
          size="small"
          fullWidth
          value={find}
          onChange={(e) => setFind(e.target.value)}
          placeholder={`Find ${noun}…`}
          inputProps={{ "aria-label": `Find ${noun}` }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ mt: 0.5 }} role="listbox" aria-multiselectable="true">
          {shown.map((o) => (
            <Box key={o.value} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <FormControlLabel
                sx={{ flex: 1, minWidth: 0, mr: 0, "& .MuiTypography-root": { fontSize: 12 } }}
                control={<Checkbox size="small" checked={checked(o.value)} onChange={() => toggle(o.value)} />}
                label={
                  <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {o.label}
                  </Box>
                }
              />
              <Typography sx={{ fontSize: 11, color: "text.secondary", whiteSpace: "nowrap" }}>{o.count}</Typography>
            </Box>
          ))}
          {shown.length ? null : (
            <Typography sx={{ fontSize: 12, color: "text.secondary", p: 1 }}>No {noun} match that.</Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default MultiSelectFilter;
